import React, { useEffect, useState } from "react";
import { Plus, Trash2, Save, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/adminApi";

/**
 * Generic admin CRUD page — list view + inline editor modal.
 *
 * Field schema:
 *   { name, label, type: "text"|"textarea"|"number"|"url"|"image", required }
 *
 * Identifier may be "id" or another key (e.g. "slug" for works).
 */
export default function AdminGenericCRUDPage({
  title,
  subtitle,
  endpoint,            // e.g. "/admin/clients"
  identifier = "id",
  fields = [],
  defaultItem = {},
  testIdPrefix = "crud",
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);  // null | {} | item
  const [saving, setSaving] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Yüklenemedi"); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, [endpoint]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (item) => {
    if (!window.confirm("Silmek istediğine emin misin?")) return;
    try {
      await api.delete(`${endpoint}/${item[identifier]}`);
      toast.success("Silindi");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const isUpdate = !!editing[identifier] && items.find(i => i[identifier] === editing[identifier]);
      if (isUpdate) {
        await api.put(`${endpoint}/${editing[identifier]}`, editing);
      } else {
        await api.post(endpoint, editing);
      }
      toast.success("Kaydedildi");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kayıt hatası"); }
    finally { setSaving(false); }
  };

  const onUpload = async (fieldName, file) => {
    const buf = await file.arrayBuffer();
    const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
    try {
      const { data } = await api.post("/admin/upload", {
        filename: file.name, content_type: file.type || "image/png",
        data_base64: b64, folder: testIdPrefix,
      });
      setEditing((p) => ({ ...p, [fieldName]: data.url }));
      toast.success("Görsel yüklendi");
    } catch (e) { toast.error("Yüklenemedi"); }
  };

  const renderField = (f) => {
    const v = editing?.[f.name] ?? "";
    const set = (val) => setEditing((p) => ({ ...p, [f.name]: val }));
    if (f.type === "textarea") return (
      <textarea data-testid={`${testIdPrefix}-field-${f.name}`} rows={f.rows || 4} value={v}
        onChange={(e) => set(e.target.value)} className="input-base resize-y" />
    );
    if (f.type === "number") return (
      <input type="number" data-testid={`${testIdPrefix}-field-${f.name}`} value={v}
        onChange={(e) => set(Number(e.target.value))} className="input-base" />
    );
    if (f.type === "image") return (
      <div className="space-y-2">
        <input type="text" value={v} onChange={(e) => set(e.target.value)}
          placeholder="/api/files/... veya /uploads/.../file.png" className="input-base text-xs" />
        <input type="file" accept="image/*"
          onChange={(e) => e.target.files?.[0] && onUpload(f.name, e.target.files[0])}
          className="text-xs" data-testid={`${testIdPrefix}-upload-${f.name}`} />
        {v ? <img src={v} alt="" className="h-20 w-auto rounded-md border border-mist" /> : null}
      </div>
    );
    if (f.type === "json") return (
      <textarea rows={f.rows || 6} value={typeof v === "string" ? v : JSON.stringify(v, null, 2)}
        onChange={(e) => {
          try { set(JSON.parse(e.target.value)); } catch { set(e.target.value); }
        }}
        className="input-base resize-y font-mono text-xs"
        data-testid={`${testIdPrefix}-field-${f.name}`} />
    );
    return (
      <input type={f.type || "text"} data-testid={`${testIdPrefix}-field-${f.name}`} value={v}
        onChange={(e) => set(e.target.value)} className="input-base" />
    );
  };

  return (
    <div data-testid={`${testIdPrefix}-page`}>
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold">{title}</h1>
          {subtitle ? <p className="mt-1.5 text-sm text-ink/55">{subtitle}</p> : null}
        </div>
        <button type="button" onClick={() => setEditing({ ...defaultItem })}
          data-testid={`${testIdPrefix}-new-btn`}
          className="btn-gold inline-flex items-center gap-2 !py-2.5 !px-4 !text-xs">
          <Plus className="h-4 w-4" /> Yeni
        </button>
      </div>

      <div className="rounded-2xl bg-white border border-mist overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-ink/50"><Loader2 className="h-5 w-5 animate-spin inline" /> Yükleniyor…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-ink/50">Henüz kayıt yok. Üstten "Yeni" ile ekle.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-mist/40 border-b border-mist text-xs uppercase tracking-[0.14em] text-ink/55">
              <tr>{fields.slice(0, 4).map(f => <th key={f.name} className="text-start p-3">{f.label}</th>)}<th className="p-3 w-32"></th></tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it[identifier]} className="border-b border-mist/60 hover:bg-mist/20"
                    data-testid={`${testIdPrefix}-row-${it[identifier]}`}>
                  {fields.slice(0, 4).map(f => (
                    <td key={f.name} className="p-3 align-middle">
                      {f.type === "image" && it[f.name] ? (
                        <img src={it[f.name]} alt="" className="h-10 w-auto rounded-md border border-mist" />
                      ) : (
                        <span className="line-clamp-2">{
                          typeof it[f.name] === "object" ? JSON.stringify(it[f.name]).slice(0, 60) :
                          String(it[f.name] ?? "").slice(0, 80)
                        }</span>
                      )}
                    </td>
                  ))}
                  <td className="p-3 text-end">
                    <button type="button" onClick={() => setEditing({ ...it })}
                      className="text-xs text-ink/70 hover:text-ink mr-3"
                      data-testid={`${testIdPrefix}-edit-${it[identifier]}`}>Düzenle</button>
                    <button type="button" onClick={() => remove(it)}
                      className="text-xs text-destructive hover:text-destructive/80 inline-flex items-center gap-1"
                      data-testid={`${testIdPrefix}-delete-${it[identifier]}`}>
                      <Trash2 className="h-3 w-3" /> Sil
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Editor modal */}
      {editing !== null && (
        <div className="fixed inset-0 z-[80] bg-ink/60 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
             onClick={() => setEditing(null)}>
          <div onClick={(e) => e.stopPropagation()}
               className="w-full max-w-3xl rounded-3xl bg-cream p-6 sm:p-8 my-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display text-xl font-bold">
                {editing[identifier] ? "Düzenle" : "Yeni Kayıt"}
              </h2>
              <button type="button" onClick={() => setEditing(null)} className="rounded-full p-2 hover:bg-mist">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-5">
              {fields.map((f) => (
                <div key={f.name}>
                  <label className="label-base">{f.label}{f.required ? " *" : ""}</label>
                  {renderField(f)}
                  {f.help ? <p className="mt-1 text-xs text-ink/45">{f.help}</p> : null}
                </div>
              ))}
            </div>
            <div className="mt-8 flex justify-end gap-3">
              <button type="button" onClick={() => setEditing(null)}
                className="btn-outline-ink !text-xs !py-2.5">İptal</button>
              <button type="button" onClick={save} disabled={saving}
                className="btn-gold !text-xs !py-2.5 disabled:opacity-60"
                data-testid={`${testIdPrefix}-save-btn`}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Kaydet
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
